import { defineStore } from 'pinia';
import { useAuthStore } from './auth';
import { erebus } from 'boot/axios';
import {
  IGenericMedication,
  IMedication,
  IMedicationCustomField,
  IMedicationTransaction,
} from '../logic/medication';
import { AxiosResponse } from 'axios';
import { STATUS } from 'src/types/base';
import {
  ROLE_ADMIN,
  ROLE_DOCTOR,
  ROLE_MASTER_ADMIN,
  ROLE_MEDIC,
  ROLE_MEDICAL_DIRECTOR,
  ROLE_QUALITY,
} from 'src/logic/auth';
import { useMemberStore } from 'stores/member';
import { useApplicationStore } from 'stores/application';
import { IMedicationReportCriteria } from 'src/models/medication';
import type { BulkUploadParams, BulkUploadResult } from 'src/models/medkit';

// Re-export types for backwards compatibility with existing imports
export type { BulkMedication, BulkUploadError, BulkUploadResult } from 'src/models/medkit';

function filterUnique(value: unknown, index: number, arr: Array<unknown>): boolean {
  return arr.indexOf(value) === index;
}

export const useMedicationStore = defineStore('Medication', {
  persist: true,

  state: (): MedicationState => ({
    pendingMedications: [] as IMedication[],
    medications: [] as IMedication[],
    pendingCustomFields: [] as IMedicationCustomField[],
    customFields: [] as IMedicationCustomField[],
    pendingTransactions: [] as IMedicationTransaction[],
    isLoading: false,
    loadingMedications: [] as string[],
    genericMedications: [] as IGenericMedication[],
    loadingGenerics: false,
  }),
  getters: {
    getMedicationCategories: (state) =>
      state.medications.map((medication: IMedication) => medication.category).filter(filterUnique),
    getMedicationLocations: (state) => (locationID: number) => {
      return state.medications
        .filter((med: IMedication) => med.locationID === locationID)
        .map((med: IMedication) => med.location)
        .filter(filterUnique);
    },
    getLocationMedications: (state) => (locationID: number) => {
      return state.medications.filter((med: IMedication) => med.locationID === locationID);
    },
    getMedicationByGUID: (state) => (medID: string) => {
      const result: IMedication | undefined = state.medications.find(
        (medication: IMedication) => medication.ID === medID,
      );
      return result;
    },
    getClientMedications:
      (state) =>
      (clientID: number): IMedication[] => {
        return state.medications.filter((med: IMedication): boolean => med.clientID === clientID);
      },
    getMedicationCustomFields: (state): Array<IMedicationCustomField> => {
      return state.customFields;
    },
    getMedicationCustomField:
      (state) =>
      (id: string): IMedicationCustomField | undefined => {
        return state.customFields.find((field: IMedicationCustomField) => field.ID === id);
      },
  },
  actions: {
    getClientID(): number {
      const authStore = useAuthStore();
      return parseInt(authStore.clientId.toString());
    },
    saveMedicationLocally(newMed: IMedication): void {
      if (newMed.expiryDate) {
        newMed.expiryDate = new Date(newMed.expiryDate);
      }
      const medIndex: number = this.medications.findIndex(
        (medication: IMedication) => medication.ID === newMed.ID,
      );
      if (medIndex >= 0) {
        this.medications[medIndex] = newMed;
      } else {
        this.medications.push(newMed);
      }
    },
    saveMedicationsLocally(newMeds: Array<IMedication>): void {
      newMeds.forEach((newMed: IMedication) => this.saveMedicationLocally(newMed));
    },
    saveCustomFieldLocally(customField: IMedicationCustomField): void {
      const i: number = this.customFields.findIndex(
        (field: IMedicationCustomField): boolean => field.ID === customField.ID,
      );
      if (i === -1) {
        this.customFields.push(customField);
      } else {
        this.customFields[i] = customField;
      }
    },
    saveCustomFieldsLocally(customFields: Array<IMedicationCustomField>): void {
      customFields.forEach((customField: IMedicationCustomField) => {
        this.saveCustomFieldLocally(customField);
      });
    },
    savePendingCustomField(customField: IMedicationCustomField): void {
      const i: number = this.pendingCustomFields.findIndex(
        (field: IMedicationCustomField) => field.ID === customField.ID,
      );
      if (i === -1) {
        this.pendingCustomFields.push(customField);
      } else {
        this.pendingCustomFields[i] = customField;
      }
    },
    savePendingCustomFields(customFields: Array<IMedicationCustomField>): void {
      customFields.forEach((customField: IMedicationCustomField) => {
        this.savePendingCustomField(customField);
      });
    },
    removePendingCustomField(GUID: string): void {
      this.pendingCustomFields = this.pendingCustomFields.filter(
        (field: IMedicationCustomField) => field.ID !== GUID,
      );
    },
    savePendingTransaction(transaction: IMedicationTransaction): void {
      transaction.status = STATUS.PENDING;
      const i: number = this.pendingTransactions.findIndex(
        (t: IMedicationTransaction) => t.ID === transaction.ID,
      );
      if (i === -1) {
        this.pendingTransactions.push(transaction);
        return;
      }
      this.pendingTransactions[i] = transaction;
    },
    removePendingTransaction(ID: string): void {
      this.pendingTransactions = this.pendingTransactions.filter(
        (t: IMedicationTransaction) => t.ID !== ID,
      );
    },
    savePendingMedication(medication: IMedication): void {
      const i = this.pendingMedications.findIndex((med: IMedication) => med.ID === medication.ID);
      if (i === -1) {
        this.pendingMedications.push(medication);
        return;
      }
      this.pendingMedications[i] = medication;
    },
    removePendingMedication(ID: string): void {
      this.pendingMedications = this.pendingMedications.filter((med: IMedication) => med.ID !== ID);
    },
    async fetchMedications() {
      try {
        const apiRes = await erebus.get(`/pillpit/medications/`);
        this.saveMedicationsLocally(apiRes.data);
      } catch (err) {
        console.error(err);
      }
    },
    async deleteMedication(medID: string) {
      try {
        await erebus.delete(`/pillpit/medications/${medID}`);
        this.medications = this.medications.filter((med: IMedication): boolean => med.ID !== medID);
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    async bulkDelete(IDs: string[]): Promise<void> {
      await erebus.delete('/pillpit/medications/', {
        data: IDs,
      });

      this.medications = this.medications.filter(
        (med: IMedication): boolean => !IDs.includes(med.ID),
      );
    },
    async fetchGenericMedications(): Promise<void> {
      if (this.loadingGenerics) {
        return;
      }
      this.genericMedications = [];
      this.loadingGenerics = true;
      try {
        const res = await erebus.get(`/pillpit/medications/generic`);
        this.genericMedications = res.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingGenerics = false;
      }
    },
    async fetchMedication(medicationID: string): Promise<void> {
      if (this.loadingMedications.some((med: string) => med === medicationID)) return;
      this.loadingMedications.push(medicationID);
      try {
        const apiRes = await erebus.get(`/pillpit/medications/${medicationID}`);
        this.saveMedicationLocally(apiRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingMedications = this.loadingMedications.filter(
          (med: string) => med !== medicationID,
        );
      }
    },
    async fetchLocationMedications(locationID: number, clientID?: number) {
      this.isLoading = true;
      if (clientID == undefined) {
        clientID = this.getClientID();
      }
      try {
        const apiRes = await erebus.get(
          `/pillpit/medications/client/${clientID}/location/${locationID}`,
        );
        this.saveMedicationsLocally(apiRes.data);
        return apiRes.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchClientMedications(clientID: number): Promise<IMedication[]> {
      const res: AxiosResponse<IMedication[]> = await erebus.get(
        `/pillpit/medications/client/${clientID}`,
      );
      this.saveMedicationsLocally(res.data);
      return this.getClientMedications(clientID);
    },
    async fetchMedicationTransactions(
      medGUID: string,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _clientID?: number,
    ): Promise<IMedicationTransaction[]> {
      const medication: IMedication | undefined = this.getMedicationByGUID(medGUID);
      if (medication === undefined) {
        throw new Error('Unable to find medication with GUID of ' + medGUID);
      }
      const res = await erebus.get(`/pillpit/medications/${medGUID}/transactions`);
      return res.data;
    },
    async fetchMedicationCustomFields(clientID?: number) {
      if (clientID === undefined) {
        clientID = this.getClientID();
      }
      void clientID;
      try {
        const res = await erebus.get(`/pillpit/custom-fields/`);
        this.saveCustomFieldsLocally(res.data);
      } catch (err) {
        console.error(err);
      }
    },
    async fetchMedicationReport(criteria: IMedicationReportCriteria): Promise<IMedication[]> {
      if (criteria.clientID === null) {
        criteria.clientID = this.getClientID();
      }
      let url = `/pillpit/medications/report?`;
      if (criteria.clientID) {
        url += `clientID=${criteria.clientID}&`;
      }
      if (criteria.locationID) {
        url += `locationID=${criteria.locationID}&`;
      }
      if (criteria.startDate) {
        url += `startDate=${criteria.startDate.toISOString()}&`;
      }
      if (criteria.endDate) {
        url += `endDate=${criteria.endDate.toISOString()}&`;
      }
      if (criteria.includeDeleted !== null) {
        url += `includeDeleted=${criteria.includeDeleted}&`;
      }
      url = url.slice(0, -1); // remove trailing &
      const res: AxiosResponse<IMedication[], void> = await erebus.get(url);
      return res.data;
    },
    async saveMedication(med: IMedication): Promise<void> {
      try {
        this.removePendingMedication(med.ID);
        const apiRes: AxiosResponse<IMedication> = await erebus.post(`/pillpit/medications/`, med);
        this.saveMedicationLocally(apiRes.data);
      } catch (err) {
        this.savePendingMedication(med);
        console.error('Error Posting Medication: ', err);
      }
    },
    async saveMedicationTransaction(transaction: IMedicationTransaction): Promise<void> {
      try {
        this.removePendingTransaction(transaction.ID);
        await erebus.post(
          `/pillpit/medications/${transaction.medicationID}/transactions`,
          transaction,
        );
      } catch (err) {
        if ((err as { response?: { status: number } }).response?.status === 400) {
          throw err;
        }
        this.savePendingTransaction(transaction);
        console.error('Error Posting Medication Transaction: ', err);
      }
    },
    async saveMedicationCustomField(customField: IMedicationCustomField) {
      try {
        this.removePendingCustomField(customField.ID);
        const apiRes: AxiosResponse<IMedicationCustomField> = await erebus.post(
          `/pillpit/custom-fields/`,
          customField,
        );
        this.saveCustomFieldLocally(apiRes.data);
      } catch (err) {
        this.savePendingCustomField(customField);
        console.error('Error Posting Medication Custom Field: ', err);
      }
    },
    async getPrescriptionTransactions(
      medID: string,
      prescriptionGUID: string,
    ): Promise<IMedicationTransaction[]> {
      const res = await erebus.get(
        `/pillpit/medications/${medID}/prescriptions/${prescriptionGUID}/transactions`,
      );
      return res.data;
    },
    async deleteMedicationCustomField(customField: IMedicationCustomField) {
      // Sync first
      await this.sync();
      await erebus.delete(`/pillpit/custom-fields/${customField.ID}`, {
        url: `/pillpit/custom-fields/${customField.ID}`,
        data: customField,
      });
      this.customFields = this.customFields.filter(
        (curField: IMedicationCustomField) => curField.ID !== customField.ID,
      );
      this.medications = this.medications.map((medication: IMedication) => {
        if (medication.customValues == null) {
          return medication;
        }
        medication.customValues = medication.customValues.filter((field) => {
          return field.ID !== customField.ID;
        });
        return medication;
      });
      await this.dataRefresh(); // medications will have changed dramatically after a deletion
    },

    async bulkUpload(data: BulkUploadParams): Promise<BulkUploadResult> {
      const response = await erebus.post<BulkUploadResult>(
        `/pillpit/medications/bulk-upload`,
        data,
      );
      this.clearLocationMedications(data.clientID, data.locationID);
      this.fetchLocationMedications(data.locationID, data.clientID);
      return response.data;
    },

    clearLocationMedications(clientID: number, locationID: number): void {
      this.medications = this.medications.filter((med: IMedication) => {
        return med.clientID !== clientID || med.locationID !== locationID;
      });
    },
    async fetchOwnMedications(): Promise<void> {
      const applicationStore = useApplicationStore();
      const authStore = useAuthStore();
      const clientID = parseInt(authStore.clientId.toString());
      const roles = [
        ROLE_ADMIN,
        ROLE_MASTER_ADMIN,
        ROLE_QUALITY,
        ROLE_DOCTOR,
        ROLE_MEDIC,
        ROLE_MEDICAL_DIRECTOR,
      ];

      if (applicationStore.useGenericMeds) {
        await this.fetchGenericMedications();
        return;
      }

      if (authStore.hasAnyRole(roles, clientID)) {
        // If they have any client roles, we get all the client's medications
        await this.fetchClientMedications(clientID);
        return;
      }

      // Otherwise, we get their location's medications
      const memberStore = useMemberStore();
      const locationID = parseInt(memberStore.employeePrimaryWorkLocationID.toString());
      await this.fetchLocationMedications(locationID, clientID);
    },
    async fetchOwnCustomFields(): Promise<void> {
      const authStore = useAuthStore();
      const clientID = parseInt(authStore.clientId.toString());
      await this.fetchMedicationCustomFields(clientID);
    },
    async dataRefresh() {
      this.medications = [];
      this.customFields = [];
      this.pendingMedications = [];
      this.pendingCustomFields = [];
      this.pendingTransactions = [];
      await this.fetchOwnMedications();
      await this.fetchOwnCustomFields();
    },

    async sync() {
      await Promise.all(
        this.pendingTransactions.map(async (transaction: IMedicationTransaction): Promise<void> => {
          await this.saveMedicationTransaction(transaction);
        }),
      );
      await Promise.all(
        this.pendingCustomFields.map(async (customField: IMedicationCustomField): Promise<void> => {
          await this.saveMedicationCustomField(customField);
        }),
      );
      await Promise.all(
        this.pendingMedications.map(async (medication: IMedication): Promise<void> => {
          await this.saveMedication(medication);
        }),
      );
    },
  },
});

export interface MedicationState {
  pendingMedications: Array<IMedication>;
  medications: Array<IMedication>;
  pendingCustomFields: Array<IMedicationCustomField>;
  customFields: Array<IMedicationCustomField>;
  pendingTransactions: Array<IMedicationTransaction>;
  isLoading: boolean;
  loadingMedications: Array<string>;
  genericMedications: Array<IGenericMedication>;
  loadingGenerics: boolean;
}

export interface SaveOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveLocally: (arg: any) => void;
  item: object;
  postPath: string;
}
